import { useState } from "react";
import { authApi } from "../api/auth";
import { loginEvent } from "../components/mixPanelEvents/mixPanelEvents";
import { EMAIL_REGEX, DEFAULT_LOGIN_ERROR, ROUTES } from "../helpers/constants";

interface UseLoginFormReturn {
    username: string;
    password: string;
    remember: boolean;
    error: string;
    loading: boolean;
    emailValid: boolean | null;
    isFormValid: boolean;
    setRemember: (value: boolean) => void;
    setError: (value: string) => void;
    handleEmailChange: (value: string) => void;
    handlePasswordChange: (value: string) => void;
    handleSubmit: (e: React.FormEvent) => void;
}

export const useLoginForm = (initialEmail: string, redirectTo: string): UseLoginFormReturn => {
    const [username, setUsername] = useState(initialEmail);
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [emailValid, setEmailValid] = useState<boolean | null>(null);

    const isFormValid = username.length > 0 && password.length > 0;

    const handleEmailChange = (value: string) => {
        setUsername(value);
        setError("");

        // Match original login.js behavior:
        // - valid only when value matches email regex
        // - invalid in all other cases (including empty after clearing)
        // - null only on initial state (before any input)
        if (value === "") {
            setEmailValid(null);
            return;
        }

        const looksLikeEmail = value.includes("@") || value.includes(".");
        const isValidEmail = EMAIL_REGEX.test(value.trim());

        if (looksLikeEmail && isValidEmail) {
            setEmailValid(true);
        } else {
            setEmailValid(false);
        }
    };

    const handlePasswordChange = (value: string) => {
        setPassword(value);
        setError("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username || !password) return;

        setError("");
        setLoading(true);

        try {
            const response = await authApi.login(username, password, remember);

            if (response.data.success) {
                loginEvent();
                localStorage.removeItem("hideAppBar");

                let destination = redirectTo || ROUTES.COURSES;
                if (window.location.hash) {
                    destination += window.location.hash;
                }
                window.location.replace(
                    `${window.location.origin}/${destination.replace(/^\//, "")}`
                );
            } else {
                setError(response.data.message);
                setEmailValid(false);
            }
        } catch (err: any) {
            setError(err?.data?.message || DEFAULT_LOGIN_ERROR);
            setEmailValid(false);
        } finally {
            setLoading(false);
        }
    };

    return {
        username,
        password,
        remember,
        error,
        loading,
        emailValid,
        isFormValid,
        setRemember,
        setError,
        handleEmailChange,
        handlePasswordChange,
        handleSubmit,
    };
};
